import styled from 'styled-components';
import React, {FC} from 'react';

import {
  fontSizesMixins,
  fontWeights,
  mediaBreakpointDown,
  mediaBreakpointOnly,
  mediaBreakpointUp,
  palette
} from '../../../../lib/styleVariables';
import {fontFamiliesR4} from '../../../../lib/styleVariablesR4';
import {Handler} from '../../../../lib/typeHelpers';
import {FieldButton, PrimaryLink, SecondaryLink, TertiaryLink} from '../../../molecules/buttons';
import {Icon} from '../../../molecules/icon';
import {Input} from '../../../molecules/inputs';
import {NavigationCallToActionSimpleBase} from './navigationCallToActionBase';
import {getPrimaryButtonStyles, getSecondaryButtonStyles} from './navigationCallToActionTwoButtons';
import {NavigationCallToActionSectionTitle} from './navigationCallToActionSectionTitle';
import {useTranslation} from '../../../../context/translation';

interface NavigationCallToActionLargeProps {
  color?: 'gray' | 'black' | 'blue';
  title: React.ReactNode;
  bullets?: string[];
  inputPlaceholder?: string;
  buttonLabel?: string;
  onSubmit?: Handler;
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  tertiaryLabel?: string;
  tertiaryHref?: string;
}

const StyledNavigationCallToActionSimpleBase = styled(NavigationCallToActionSimpleBase)`
  display: flex;
  justify-content: center;
  padding: 70px 40px 80px;

  ${mediaBreakpointDown('mobile')} {
    padding: 32px;
  }
  ${mediaBreakpointOnly('tablet')} {
    padding-top: 50px;
    padding-bottom: 50px;
  }
`;

const StyledSubHeroTitle = styled(NavigationCallToActionSectionTitle)`
  margin: 0;
  ${fontSizesMixins.h1()};
  font-family: ${fontFamiliesR4.body};
  font-weight: ${fontWeights.semibold};

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixins.h2()};
    font-family: ${fontFamiliesR4.body};
    font-weight: ${fontWeights.semibold};
  }
`;

const StyledContentDiv = styled.div`
  max-width: 704px;
  text-align: center;
  padding: 0 16px;
  ${mediaBreakpointDown('mobile')} {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
  }
`;

const StyledBulletList = styled.ul`
  list-style-type: none;
  padding: 0;
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 0;
  ${mediaBreakpointDown('mobile')} {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledBulletListItem = styled.li<{color?: string}>`
  ${fontSizesMixins.text3()};
  color: ${props => (props.color === 'black' ? palette.white.highlight : '')};
  display: flex;
  font-weight: ${fontWeights.bold};

  ${mediaBreakpointUp('tablet')} {
    &:not(:last-child) {
      margin-right: 26px;
    }
  }
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: 8px;
  margin-top: 7px;
`;

const StyledFieldButton = styled(FieldButton)<{ctaTheme?: string}>`
  margin-left: 8px;
  border: 3px solid ${props => (props.ctaTheme === 'black' ? palette.white.highlight : palette.black.base)};
  padding-top: 8px;
  padding-bottom: 8px;
  transition-property: background, color, box-shadow;
  line-height: 113%;

  ${mediaBreakpointDown('mobile')} {
    margin-top: 16px;
    margin-left: 0;
    width: 100%;
  }

  &:hover {
    background: ${palette.white.highlight};
    color: ${palette.black.base};
  }
`;

const StyledInput = styled(Input)`
  width: 350px;
  max-width: 100%;
  ${mediaBreakpointDown('mobile')} {
    width: 100%;
  }
`;

const StyledForm = styled.form`
  margin-top: 40px;
  ${mediaBreakpointDown('mobile')} {
    margin-top: 20px;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  ${mediaBreakpointDown('mobile')} {
    width: 100%;
    flex-direction: column;
    margin-top: 27px;
  }
`;

const buttonStyle = `
  transition-property: background, color, box-shadow;
  padding-top: 15px;
  padding-bottom: 15px;
  line-height: 18px;
  ${mediaBreakpointDown('mobile')} {
    width: 100%;
    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }

  ${mediaBreakpointUp('tablet')} {
    &:not(:last-child) {
      margin-right: 16px;
    }
  }
`;

const tertiaryLinkStyle = `
  color: ${palette.white.base};
  ${mediaBreakpointDown('tablet')} {
    display: flex;
    justify-content: center;
  }
`;

const StyledPrimaryLink = styled(PrimaryLink)<{$css?: string}>`
  ${buttonStyle}
  ${props => props.$css}
  box-shadow: none;
`;

const StyledSecondaryLink = styled(SecondaryLink)<{$css?: string}>`
  ${buttonStyle}
  ${props => props.$css}
`;

const StyledTertiaryLink = styled(TertiaryLink)`
  ${tertiaryLinkStyle}
`;

const StyledTertiaryLinkDesciption = styled.div`
  ${tertiaryLinkStyle}
  ${fontSizesMixins.text3()}
  margin-right: 3px;
`;

export const NavigationCallToActionLarge: FC<NavigationCallToActionLargeProps> = props => {
  const {
    color,
    title,
    bullets,
    onSubmit,
    inputPlaceholder,
    buttonLabel,
    primaryHref,
    primaryLabel,
    secondaryHref,
    secondaryLabel,
    tertiaryHref,
    tertiaryLabel
  } = props;

  const hasForm = inputPlaceholder && buttonLabel;
  const hasPrimaryButton = primaryHref && primaryLabel;
  const hasSecondaryButton = secondaryHref && secondaryLabel;
  const hasAtLeastOneButton = hasPrimaryButton || hasSecondaryButton;

  const {t} = useTranslation();

  return (
    <StyledNavigationCallToActionSimpleBase color={color || ''}>
      <StyledContentDiv>
        <StyledSubHeroTitle color={color}>{title}</StyledSubHeroTitle>
        {bullets && (
          <StyledBulletList>
            {bullets.map(item => (
              <StyledBulletListItem key={item} color={color}>
                <StyledIcon name="checkmark" />
                <span>{item}</span>
              </StyledBulletListItem>
            ))}
          </StyledBulletList>
        )}

        {hasForm && (
          <StyledForm
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();

              if (onSubmit)
                onSubmit();
            }}
          >
            <StyledInput onDarkBackground placeholder={inputPlaceholder} type="email" />
            <StyledFieldButton ctaTheme={color} isHover>
              {buttonLabel}
            </StyledFieldButton>
          </StyledForm>
        )}

        {!hasForm && hasAtLeastOneButton && (
          <StyledButtonContainer>
            {hasPrimaryButton && (
              <StyledPrimaryLink href={props.primaryHref} $css={getPrimaryButtonStyles(color)}>
                {props.primaryLabel}
              </StyledPrimaryLink>
            )}

            {hasSecondaryButton && (
              <StyledSecondaryLink href={props.secondaryHref} $css={getSecondaryButtonStyles(color)}>
                {props.secondaryLabel}
              </StyledSecondaryLink>
            )}
          </StyledButtonContainer>
        )}
        {tertiaryHref && (
          <StyledButtonContainer>
            <StyledTertiaryLinkDesciption>
              {t('request-roi.navigation-cta-text')}
            </StyledTertiaryLinkDesciption>
            <StyledTertiaryLink href={tertiaryHref}>{tertiaryLabel}</StyledTertiaryLink>
          </StyledButtonContainer>
        )}
      </StyledContentDiv>
    </StyledNavigationCallToActionSimpleBase>
  );
};
